/* eslint-disable no-console */
import { Alert, Box, Button, Spinner, Text, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';

import { typeguards } from '@finley-technologies/finley-types';

import {
  useCreateAssetVerificationDocumentMutation,
  useCreateAssetVerificationRecordMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { JSONEditor } from './JSONEditor';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import {
  useGetAssetVerificationDocumentByRawIdQuery,
  GetAssetVerificationReceivablesByMetadataDocumentGQLType,
  GetAssetVerificationDocumentByRawIdDocumentGQLType,
} from 'packages/verifications/requests/requests.gql.generated';
import { AssetVerificationEditorProps } from 'packages/verifications/ui/AssetVerificationReceivablesTable/types';

const convertErrorToString = (e: any): string => {
  // if there was a message there return it, otherwise stringify.
  const message: string = 'message' in e ? e.message : JSON.stringify(e);
  return message;
};

type Props = AssetVerificationEditorProps & {
  onClose: () => void;
};
export const AssetVerificationEditor = ({
  receivableDocumentationRawId,
  creditFacilityId,
  reportingDate,
  fileVersion,
  artifactDocumentId,
  finleyReceivableId,
  onClose,
}: Props) => {
  const toast = useToast();
  const [document, setDocument] = useState<object | null>(null);
  const { loading } = useGetAssetVerificationDocumentByRawIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      receivableDocumentationRawId,
    },
    onCompleted: (data) => {
      const { assetVerificationDocumentByRawId } = data;

      if (!assetVerificationDocumentByRawId) {
        setDocument({
          fields: [],
        });
      } else {
        const documentToEdit = JSON.parse(assetVerificationDocumentByRawId);
        setDocument(documentToEdit);
      }
    },
  });
  const [message, setMessage] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // this mutation creates a new asset verification document
  // (the documentation that is used to verify the receivables)
  const [
    createAssetVerificationDocument,
  ] = useCreateAssetVerificationDocumentMutation({
    refetchQueries: [GetAssetVerificationDocumentByRawIdDocumentGQLType],
    onCompleted: (data) => {
      console.log('createAssetVerificationDocument completed:', data);
    },
    onError: (error: any) => {
      setServerError(convertErrorToString(error));
    },
  });

  // this mutation creates a verification record.
  // (this will inspect loan tape against documentation and store results in db)
  const [
    createAssetVerificationRecord,
  ] = useCreateAssetVerificationRecordMutation({
    refetchQueries: [GetAssetVerificationReceivablesByMetadataDocumentGQLType],
    onCompleted: (data) => {
      console.log('createAssetVerificationRecord completed:', data);
    },
    onError: (error: any) => {
      setServerError(convertErrorToString(error));
    },
  });

  if (loading) {
    return <Spinner />;
  }

  if (!document) {
    return <Text>Document not found</Text>;
  }

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column">
      <JSONEditor
        readOnly={false}
        contents={document}
        nodeId=""
        schema={typeguards.assetVerification.getAssetVerificationStoredDocumentSchema()}
        autoSave={false}
        onSave={async (_id: string, newValue: object) => {
          setMessage('Saving Updated Documentation...');
          await createAssetVerificationDocument({
            variables: {
              receivableDocumentationRawId,
              assetVerificationDocument: JSON.stringify(newValue),
              note: 'test',
            },
          });

          // now that we have a new asset documentation
          // run verifcation against it to create new verification record.
          setMessage('Creating Verification Record...');
          await createAssetVerificationRecord({
            variables: {
              creditFacilityId,
              reportingDate,
              fileVersion,
              artifactDocumentId,
              finleyReceivableId,
            },
          });
          setMessage(null);
        }}
      >
        <>
          {serverError && <Alert status="error">{serverError}</Alert>}
          {message && <Alert>{message}</Alert>}
          <Button onClick={onClose}>Close</Button>
        </>
      </JSONEditor>
    </Box>
  );
};
